import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';

const SizeGuide = () => {
  const sizeChart = {
    measurements: ['Bust (in)', 'Waist (in)', 'Hips (in)'],
    sizes: {
      'XS': ['32-33', '24-25', '34-35'],
      'S': ['34-35', '26-27', '36-37'],
      'M': ['36-37', '28-29', '38-39'],
      'L': ['38-39', '30-31', '40-41'],
      'XL': ['40-41', '32-33', '42-43']
    }
  };

  const measurementGuide = [
    {
      title: 'Bust',
      instruction: 'Measure around the fullest part of your chest, keeping the tape parallel to the ground.'
    },
    {
      title: 'Waist',
      instruction: 'Measure around the narrowest part of your natural waist, usually just above the belly button.'
    },
    {
      title: 'Hips',
      instruction: 'Measure around the fullest part of your hips, keeping the tape parallel to the ground.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Size Guide | Panchali Design Hub</title>
        <meta name="description" content="Comprehensive size guide and measurement instructions for Panchali Design Hub's clothing" />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl font-light mb-4">Size Guide</h1>
          <div className="w-24 h-[2px] bg-gradient-to-r from-indigo-900 to-transparent mx-auto"></div>
        </motion.div>

        {/* Size Chart */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16"
        >
          <h2 className="text-2xl font-light mb-6">Standard Size Chart</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="p-4 text-left border-b">Size</th>
                  {sizeChart.measurements.map((measurement, index) => (
                    <th key={index} className="p-4 text-left border-b">{measurement}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(sizeChart.sizes).map(([size, measurements], index) => (
                  <tr key={size} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="p-4 border-b font-medium">{size}</td>
                    {measurements.map((measurement, i) => (
                      <td key={i} className="p-4 border-b">{measurement}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.section>

        {/* Measurement Guide */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16"
        >
          <h2 className="text-2xl font-light mb-6">How to Measure</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              {measurementGuide.map((guide, index) => (
                <div key={index} className="bg-white p-6 rounded-xl shadow-sm">
                  <h3 className="text-lg font-medium mb-2">{guide.title}</h3>
                  <p className="text-gray-600">{guide.instruction}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Tips Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-50 p-6 rounded-xl"
        >
          <h2 className="text-xl font-medium mb-4">Measurement Tips</h2>
          <ul className="space-y-2 text-gray-600">
            <li>• Use a fabric measuring tape for best results</li>
            <li>• Measure over undergarments, not over clothes</li>
            <li>• Keep the measuring tape snug but not tight</li>
            <li>• When in doubt, choose the larger size</li>
          </ul>
        </motion.section>
      </div>
    </>
  );
};

export default SizeGuide;